import { ChangeDetectionStrategy, Component, inject, signal, type OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { CloudApiService, type McpGrant } from '@/cloud/services/cloud-api.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

/** The signed-in user's connected AI agent apps (MCP OAuth grants), with disconnect. Shown on the profile page. */
@Component({
    selector: 'app-connected-apps',
    standalone: true,
    imports: [DatePipe, ButtonModule, EmptyStateComponent, LoadingSkeletonComponent, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <app-section-card
            title="Connected Apps"
            subtitle="AI agents you've allowed to read tenant data on your behalf. Disconnecting one stops it immediately."
            icon="pi pi-sparkles"
            [count]="loading() ? null : grants().length"
            [flush]="true"
        >
            @if (loading()) {
                <div class="app-card-pad">
                    <app-loading-skeleton preset="list" [count]="2" label="Loading connected apps" />
                </div>
            } @else if (grants().length === 0) {
                <app-empty-state icon="pi pi-sparkles" title="No connected apps" [compact]="true" />
            } @else {
                <ul class="app-card-list">
                    @for (grant of grants(); track grant.grantId) {
                        <li class="app-card-list__item">
                            <div class="app-card-list__body">
                                <span class="app-card-list__title">{{ grant.clientName }}</span>
                                <span class="app-card-list__meta">{{ namespaceNames(grant) }}</span>
                                <span class="app-card-list__meta">
                                    Connected {{ grant.createdUtc | date: 'mediumDate' }}
                                    @if (grant.lastUsedUtc) {
                                        · last used {{ grant.lastUsedUtc | date: 'short' }}
                                    }
                                </span>
                            </div>
                            <p-button
                                label="Disconnect"
                                severity="danger"
                                [outlined]="true"
                                size="small"
                                [ariaLabel]="'Disconnect ' + grant.clientName"
                                [loading]="busy() === grant.grantId"
                                [disabled]="busy() === grant.grantId"
                                (onClick)="disconnect(grant)"
                            />
                        </li>
                    }
                </ul>
            }
        </app-section-card>
    `
})
export class ConnectedApps implements OnInit {
    private api = inject(CloudApiService);
    private messageService = inject(MessageService, { optional: true });

    grants = signal<McpGrant[]>([]);
    loading = signal(true);
    busy = signal<string | null>(null);

    ngOnInit(): void {
        this.api
            .getMyConnectedApps()
            .then((g) => this.grants.set(g))
            .catch(() => this.grants.set([]))
            .finally(() => this.loading.set(false));
    }

    namespaceNames(grant: McpGrant): string {
        return grant.namespaces.map((n) => n.name).join(', ');
    }

    disconnect(grant: McpGrant) {
        this.busy.set(grant.grantId);
        this.api
            .revokeMyConnectedApp(grant.grantId)
            .then(() => {
                this.grants.update((list) => list.filter((g) => g.grantId !== grant.grantId));
                this.messageService?.add({ severity: 'success', summary: `${grant.clientName} disconnected` });
            })
            .catch(() => this.messageService?.add({ severity: 'error', summary: 'Could not disconnect the app' }))
            .finally(() => this.busy.set(null));
    }
}
